import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth, getCsrfToken, refreshCsrfToken } from './context/AuthContext';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import HomeRouter from './components/HomeRouter';
import HistoryPage from './pages/HistoryPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProcessPage from './pages/ProcessPage';
import AccuracyDashboardPage from './pages/AccuracyDashboardPage';
import AdminUsersPage from './pages/AdminUsersPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import BlogIndexPage from './pages/BlogIndexPage';
import WhatIsCslScreening from './pages/blog/WhatIsCslScreening';
import ExportComplianceBasics from './pages/blog/ExportComplianceBasics';
import AutomatedVsManualScreening from './pages/blog/AutomatedVsManualScreening';
import Footer from './components/Footer';
import './App.css';

// Configure axios to send credentials (cookies) with requests
axios.defaults.withCredentials = true;

// Attach CSRF token to all mutating requests
axios.interceptors.request.use((config) => {
  const token = getCsrfToken();
  if (token) {
    config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

// Auto-refresh CSRF token on 403 and retry the request once
// Also retry once on 401 after refreshing CSRF (session may have rotated)
// Handle ACCOUNT_SUSPENDED by forcing logout
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle suspended account
    if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_SUSPENDED') {
      alert('Your account has been suspended. Please contact support.');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (
      (error.response?.status === 403 || error.response?.status === 401) &&
      !originalRequest._csrfRetried
    ) {
      originalRequest._csrfRetried = true;
      await refreshCsrfToken();
      originalRequest.headers['X-CSRF-Token'] = getCsrfToken();
      return axios(originalRequest);
    }
    return Promise.reject(error);
  }
);

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading, isAuthenticated, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface ComprehensiveResults {
  company_name: string;
  enrichment?: any;
  csl_results: any;
  match_validation?: any;
  domain_results?: any;
  ai_research?: any;
  opensanctions?: any;
  risk_assessment?: {
    risk_level: string;
    risk_factors: Array<string | { text: string; severity: string }>;
    recommendation: string;
    source_risks?: {
      [key: string]: {
        level: string;
        summary: string;
      };
    };
    report_id?: string;
    screening_timestamp?: string;
  };
  corporate_structure?: any;
}

function App() {
  // Shared state for PDF generation
  const [comprehensiveResults, setComprehensiveResults] = useState<ComprehensiveResults | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [loadScreeningId, setLoadScreeningId] = useState<number | null>(null);
  const [resetTrigger, setResetTrigger] = useState(0);

  const handleGenerateReport = useCallback(async () => {
    if (!comprehensiveResults && !companyName) return;

    setGeneratingReport(true);

    try {
      const response = await axios.post(
        `${API_BASE}/report/generate`,
        comprehensiveResults
          ? { results: comprehensiveResults, name: companyName }
          : { name: companyName },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance_report_${companyName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Log without sensitive data
      console.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  }, [comprehensiveResults, companyName]);

  const handleLoadScreening = useCallback((id: number) => {
    setLoadScreeningId(id);
  }, []);

  const clearLoadScreeningId = useCallback(() => {
    setLoadScreeningId(null);
  }, []);

  const handleNewScreening = useCallback(() => {
    setComprehensiveResults(null);
    setCompanyName('');
    setResetTrigger(prev => prev + 1);
  }, []);

  const canGenerateReport = Boolean(comprehensiveResults && companyName);

  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <Header
            onGenerateReport={handleGenerateReport}
            canGenerateReport={canGenerateReport}
            isGeneratingReport={generatingReport}
            onNewScreening={handleNewScreening}
          />
          <main>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/process" element={<ProcessPage />} />
              <Route
                path="/"
                element={
                  <HomeRouter
                    onResultsChange={setComprehensiveResults}
                    onCompanyNameChange={setCompanyName}
                    loadScreeningId={loadScreeningId}
                    onScreeningLoaded={clearLoadScreeningId}
                    resetTrigger={resetTrigger}
                  />
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <HistoryPage onLoadScreening={handleLoadScreening} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accuracy"
                element={
                  <AdminRoute>
                    <AccuracyDashboardPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminUsersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/what-is-csl-screening" element={<WhatIsCslScreening />} />
              <Route path="/blog/export-compliance-basics" element={<ExportComplianceBasics />} />
              <Route path="/blog/automated-vs-manual-screening" element={<AutomatedVsManualScreening />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
